import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  BarChart3,
  Building2,
  ClipboardList,
  HardHat,
  Loader2,
  Star,
  Timer,
  Wrench,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import {
  EMPTY_REPORTING_FILTERS,
  ReportingFilters,
  type ReportingFilterValues,
} from '../../components/reporting/ReportingFilters';
import { ExportButtons } from '../../components/reporting/ExportButtons';
import { DeltaBadge } from '../../components/reporting/DeltaBadge';
import { defaultDateRange, toIsoFromDate, toIsoToDate } from '../../components/reporting/dateRange';
import { formatCurrency, formatDurationH, formatNumber } from '../../components/reporting/format';
import type { OverviewResult } from '../../components/reporting/types';

/**
 * Page `/reporting` — Vue globale (US-REP-01).
 *
 * Comparaison période courante vs précédente sur 5 KPIs majeurs :
 *  - Demandes d'intervention
 *  - Interventions clôturées
 *  - Coût total
 *  - Durée moyenne
 *  - Satisfaction
 *
 * Chaque carte affiche : valeur actuelle, valeur précédente, delta % avec
 * sens (up/down/flat) — le backend gère l'inversion pour `duree_moyenne_h`
 * et `cout_total`.
 *
 * Liens vers les rapports détaillés (équipements / infrastructure / interventions)
 * + historique.
 */
const ICONS: Record<string, typeof BarChart3> = {
  total_interventions: ClipboardList,
  interventions_cloturees: HardHat,
  cout_total: Banknote,
  duree_moyenne_h: Timer,
  satisfaction: Star,
};

const INVERTED_KEYS = new Set(['cout_total', 'duree_moyenne_h']);

export function ReportingOverviewPage() {
  const def = defaultDateRange();
  const [filters, setFilters] = useState<ReportingFilterValues>({
    ...EMPTY_REPORTING_FILTERS,
    date_from: def.from,
    date_to: def.to,
  });
  const [data, setData] = useState<OverviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const printRef = useRef<HTMLDivElement | null>(null);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    const fromIso = toIsoFromDate(filters.date_from);
    const toIso = toIsoToDate(filters.date_to);
    if (fromIso) p.set('date_from', fromIso);
    if (toIso) p.set('date_to', toIso);
    return p;
  }, [filters.date_from, filters.date_to]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.toString()]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const qs = queryParams.toString();
      const res = await api.get<{ data: OverviewResult }>(
        `/reporting/overview${qs ? `?${qs}` : ''}`,
      );
      setData(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function formatKpiValue(key: string, value: number): string {
    if (key === 'cout_total') return formatCurrency(value);
    if (key === 'duree_moyenne_h') return formatDurationH(value);
    if (key === 'satisfaction') return `${value.toFixed(1)} / 5`;
    return formatNumber(value);
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="w-7 h-7 mr-2 text-primary-600" />
            Reporting — Vue globale
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Variation entre la période courante et la précédente.
          </p>
        </div>
        <ExportButtons
          excelUrl="/reporting/overview.xlsx"
          excelParams={queryParams}
          filename="reporting-overview"
          targetRef={printRef}
          pdfDisabled={!data}
        />
      </div>

      <ReportingFilters
        values={filters}
        onChange={setFilters}
        onRefresh={refresh}
        fields={{ dates: true }}
      />

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center p-24 bg-white rounded-lg shadow-sm border border-gray-200">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
        </div>
      ) : data ? (
        <div ref={printRef} className="space-y-4">
          <div className="text-xs text-gray-500">
            Période courante : {new Date(data.periode.from).toLocaleDateString('fr-FR')} →{' '}
            {new Date(data.periode.to).toLocaleDateString('fr-FR')} ({data.periode.duree_jours}{' '}
            jours) — vs précédente :{' '}
            {new Date(data.comparaison_precedente.from).toLocaleDateString('fr-FR')} →{' '}
            {new Date(data.comparaison_precedente.to).toLocaleDateString('fr-FR')}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {data.kpis.map((k) => {
              const Icon = ICONS[k.key] ?? BarChart3;
              const invert = INVERTED_KEYS.has(k.key);
              return (
                <div
                  key={k.key}
                  className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase tracking-wide text-gray-500 truncate pr-2">
                      {k.label}
                    </p>
                    <Icon className="w-4 h-4 text-primary-600 flex-shrink-0" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatKpiValue(k.key, k.value_actuel)}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      Précédent : {formatKpiValue(k.key, k.value_precedent)}
                    </span>
                    <DeltaBadge delta={k.delta_pct} sens={k.sens} invert={invert} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <SubReportLink
              to="/reporting/equipements"
              icon={Wrench}
              label="Rapport Équipements"
              description="Inventaire détaillé, conformité, MTBF/MTTR par équipement."
            />
            <SubReportLink
              to="/reporting/infrastructure"
              icon={Building2}
              label="Rapport Infrastructure"
              description="Occupation des bâtiments, surfaces, conformité ERP."
            />
            <SubReportLink
              to="/reporting/interventions"
              icon={HardHat}
              label="Rapport Interventions"
              description="Délais, SLA, coûts, satisfaction, top techniciens."
            />
            <SubReportLink
              to="/reporting/historique"
              icon={BarChart3}
              label="Historique des exports"
              description="Traçabilité des téléchargements réalisés."
            />
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Aucune donnée disponible.</p>
      )}
    </div>
  );
}

function SubReportLink({
  to,
  icon: Icon,
  label,
  description,
}: {
  to: string;
  icon: typeof BarChart3;
  label: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="block bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:border-primary-300 hover:shadow transition"
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-primary-600" />
        <ArrowRight className="w-4 h-4 text-gray-400" />
      </div>
      <p className="text-sm font-semibold text-gray-900">{label}</p>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </Link>
  );
}
